/*
 * Chat Sessions API Routes
 * Features: Session management, message CRUD, active state handling, auto-session creation
 * Logic: RESTful endpoints calling stored procedures for chat sessions and messages
 * Changes: 
 * - Added transactional edited-message replacement and history truncation
 * - Scoped direct session lookups to the authenticated context user
 * - Added constrained pending ask_user_input canonicalization by durable message row ID
 * - Initial implementation with full CRUD operations
 * - Added auto-session creation endpoint (GetOrCreateActiveSession) for seamless user experience
 * - Fixed InsertChatSession parameter name from @SetAsActive to @IsActive
 * - Added PATCH /api/data/chat/sessions/:id/active endpoint for setting active session without requiring title
 * - Modified PUT endpoint to only handle title updates (no longer handles isActive)
 */

import { z } from "zod";
import { DataRoutes } from "./types";

const chatRoutes: DataRoutes = {
  // Get all chat sessions for current user
  'get /api/data/chat/sessions': {
    query: `EXEC [dbo].[GetChatSessions]`
  },

  // Get active chat session for current user
  'get /api/data/chat/sessions/active': {
    query: `EXEC [dbo].[GetActiveSession]`
  },

  // Get or create active chat session for current user
  'get /api/data/chat/sessions/active/ensure': {
    query: `EXEC [dbo].[GetOrCreateActiveSession]`
  },

  // Get specific chat session by ID
  'get /api/data/chat/sessions/:id': {
    schema: z.object({
      id: z.number().int(),
    }),
    query: `SELECT *
      FROM [dbo].[ChatSessions]
      WHERE Id = @id
        AND UserId = dbo.fnGetContextUser()`
  },

  // Create a new chat session
  'post /api/data/chat/sessions': {
    schema: z.object({
      title: z.string().optional(),
      isActive: z.boolean().default(true),
    }),
    query: `EXEC [dbo].[InsertChatSession] @Title = @title, @IsActive = @isActive`
  },

  // Update chat session (title only - for renaming)
  'put /api/data/chat/sessions/:id': {
    schema: z.object({
      id: z.number().int(),
      title: z.string(),
    }),
    query: `EXEC [dbo].[UpdateChatSession] @Id = @id, @Title = @title`
  },

  // Set active session (dedicated endpoint for switching sessions)
  'patch /api/data/chat/sessions/:id/active': {
    schema: z.object({
      id: z.number().int(),
    }),
    query: `EXEC [dbo].[UpdateChatSession] @Id = @id, @IsActive = 1`
  },

  // Delete chat session
  'delete /api/data/chat/sessions/:id': {
    schema: z.object({
      id: z.number().int(),
    }),
    query: `EXEC [dbo].[DeleteChatSession] @Id = @id`
  },

  // Get all messages for a chat session
  'get /api/data/chat/sessions/:chatSessionId/messages': {
    schema: z.object({
      chatSessionId: z.number().int(),
    }),
    query: `EXEC [dbo].[GetChatMessages] @ChatSessionId = @chatSessionId`
  },

  // Authoritative uncached reload used after an ambiguous edit acknowledgement
  'post /api/data/chat/sessions/:chatSessionId/messages/reconcile': {
    schema: z.object({
      chatSessionId: z.number().int(),
    }),
    query: `EXEC [dbo].[GetChatMessages] @ChatSessionId = @chatSessionId`
  },

  // Get messages for LLM (excludes UI-only)
  'get /api/data/chat/sessions/:chatSessionId/messages/llm': {
    schema: z.object({
      chatSessionId: z.number().int(),
    }),
    query: `EXEC [dbo].[GetChatMessagesForLLM] @ChatSessionId = @chatSessionId`
  },

  // Insert a single message
  'post /api/data/chat/sessions/:chatSessionId/messages': {
    schema: z.object({
      chatSessionId: z.number().int(),
      messageId: z.number().int(),
      role: z.string(),
      content: z.string(),
      timestamp: z.string(),
      name: z.string().nullable().default(null),
      toolCallId: z.string().nullable().default(null),
      toolCalls: z.string().nullable().default(null),
      executionSource: z.string().nullable().default(null),
      functionData: z.string().nullable().default(null),
      isUIOnly: z.boolean().default(false),
      sseEventType: z.string().nullable().default(null),
      eventData: z.string().nullable().default(null),
    }),
    query: `EXEC [dbo].[InsertChatMessage] @ChatSessionId = @chatSessionId, @MessageId = @messageId, @Role = @role, @Content = @content, @Timestamp = @timestamp, @Name = @name, @ToolCallId = @toolCallId, @ToolCalls = @toolCalls, @ExecutionSource = @executionSource, @FunctionData = @functionData, @IsUIOnly = @isUIOnly, @SSEEventType = @sseEventType, @EventData = @eventData`
  },

  // Insert multiple messages (batch)
  'post /api/data/chat/sessions/:chatSessionId/messages/batch': {
    schema: z.object({
      chatSessionId: z.number().int(),
      messages: z.string(), // JSON array as string
    }),
    query: `EXEC [dbo].[InsertChatMessageBatch] @ChatSessionId = @chatSessionId, @MessagesJson = @messages`
  },

  // Clear all messages from session
  'delete /api/data/chat/sessions/:chatSessionId/messages': {
    schema: z.object({
      chatSessionId: z.number().int(),
    }),
    query: `EXEC [dbo].[ClearChatMessages] @ChatSessionId = @chatSessionId`
  },

  // Delete messages after specific MessageId (for editing)
  'delete /api/data/chat/sessions/:chatSessionId/messages/:messageId': {
    schema: z.object({
      chatSessionId: z.number().int(),
      messageId: z.number().int(),
    }),
    query: `EXEC [dbo].[DeleteMessagesAfter] @SessionId = @chatSessionId, @FromMessageId = @messageId`
  },

  // Update chat message content (for editing)
  'patch /api/data/chat/sessions/:chatSessionId/messages/:messageId': {
    schema: z.object({
      chatSessionId: z.number().int(),
      messageId: z.number().int(),
      content: z.string(),
    }),
    query: `EXEC [dbo].[UpdateChatMessage] @SessionId = @chatSessionId, @MessageId = @messageId, @NewContent = @content`
  },

  // Atomically update an edited message and remove its later conversation branch
  'patch /api/data/chat/sessions/:chatSessionId/messages/:messageId/edit': {
    schema: z.object({
      chatSessionId: z.number().int(),
      messageId: z.number().int(),
      content: z.string(),
    }),
    query: `
      SET XACT_ABORT ON;
      BEGIN TRANSACTION;
      BEGIN TRY
        EXEC [dbo].[UpdateChatMessage]
          @SessionId = @chatSessionId,
          @MessageId = @messageId,
          @NewContent = @content;
        EXEC [dbo].[DeleteMessagesAfter]
          @SessionId = @chatSessionId,
          @FromMessageId = @messageId;
        COMMIT TRANSACTION;
      END TRY
      BEGIN CATCH
        IF @@TRANCOUNT > 0 ROLLBACK TRANSACTION;
        THROW;
      END CATCH;
    `
  },

  // Update canonical assistant tool calls before persisting a human-input outcome
  'patch /api/data/chat/sessions/:chatSessionId/messages/:id/tool-calls': {
    schema: z.object({
      chatSessionId: z.number().int(),
      id: z.number().int().positive(),
      toolCallId: z.string().trim().min(1).max(255),
    }).strict(),
    query: `EXEC [dbo].[UpdateChatMessageToolCalls] @SessionId = @chatSessionId, @Id = @id, @ToolCallId = @toolCallId`
  },

  // Clear tool calls from a message (after user interaction completes)
  'patch /api/data/chat/sessions/:chatSessionId/messages/:messageId/clear-tool-calls': {
    schema: z.object({
      chatSessionId: z.number().int(),
      messageId: z.number().int(),
    }),
    query: `EXEC [dbo].[ClearMessageToolCalls] @SessionId = @chatSessionId, @MessageId = @messageId`
  },

  // Delete specific messages by their message IDs
  'delete /api/data/chat/sessions/:chatSessionId/messages/by-ids': {
    schema: z.object({
      chatSessionId: z.number().int(),
      messageIds: z.array(z.number().int()),
    }),
    query: `EXEC [dbo].[DeleteMessagesByIds] @SessionId = @chatSessionId, @MessageIdsJson = @messageIds`
  },
};

export default chatRoutes;
